import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { FormObjectComponent } from '../../../../shared/forms/form-object.component';
import { EstimateListComponent } from '../estimate-list/estimate-list.component';
import { DialogComponent } from '../../../../shared/dialog/dialog.component';
import { ComponentGroupService } from '../../services/form-service/component-group.service';
import { FormValidationTrackerService } from '../../services/form-service/form-validation-tracker.service';
import { FormValidation } from '../../../../shared/forms/form-validation';
import { EstimateTotalService } from '../../services/estimate-total.service';

@Component({
  selector: 'correction-estimate',
  templateUrl: './correction-estimate.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush // JJ - update for component
})
export class CorrectionEstimateComponent extends FormObjectComponent implements OnDestroy, OnInit {
  @Input({ required: true }) componentGroupKey: number;
  @Input() isReadOnly: boolean = false;
  @ViewChild(EstimateListComponent) estimateList: EstimateListComponent;
  @Output() remove = new EventEmitter<void>();

  public override form : UntypedFormGroup;
  private confirmationDialog: MatDialogRef<DialogComponent>;
  public componentGroupName: string = '';
  protected _destroying$ = new Subject<void>();

  constructor(private dialog: MatDialog,
              private componentGroupService: ComponentGroupService,
              private formValidationTracker: FormValidationTrackerService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
    });

    // Initialize component group name
    this.componentGroupName = this.componentGroupService?.getName(this.componentGroupKey) ?? '';

    // Subscribe to component group changes
    this.componentGroupService?.getSubject(this.componentGroupKey)
      ?.pipe(takeUntil(this._destroying$))
      ?.subscribe(groupName => {
        this.componentGroupName = groupName ?? '';
        this.cdr.markForCheck(); // Trigger change detection with OnPush
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  public override validateForm(): void {
    super.validateForm();
    this.estimateList?.validateForm();

    // Collect invalid fields from estimate line items
    const invalidFields: string[] = [];

    // Check all estimate line items for invalid fields
    if (this.estimateList) {
      // Check part estimates
      this.estimateList.partForms?.forEach((partForm, index) => {
        if (partForm?.form) {
          Object.keys(partForm.form.controls).forEach(key => {
            const control = partForm.form.get(key);
            if (control?.invalid) {
              // Create unique field names for line items
              const fieldName = `part_${index}_${key}`;
              invalidFields.push(fieldName);
              // Add display names for estimate fields
              this.addEstimateFieldDisplayNames(fieldName, key, 'Part', index + 1);
            }
          });
        }
      });

      // Check labor estimates
      this.estimateList.laborForms?.forEach((laborForm, index) => {
        if (laborForm?.form) {
          Object.keys(laborForm.form.controls).forEach(key => {
            const control = laborForm.form.get(key);
            if (control?.invalid) {
              // Create unique field names for line items
              const fieldName = `labor_${index}_${key}`;
              invalidFields.push(fieldName);
              // Add display names for estimate fields
              this.addEstimateFieldDisplayNames(fieldName, key, 'Labor', index + 1);
            }
          });
        }
      });

      // Check material estimates
      this.estimateList.materialForms?.forEach((materialForm, index) => {
        if (materialForm?.form) {
          Object.keys(materialForm.form.controls).forEach(key => {
            const control = materialForm.form.get(key);
            if (control?.invalid) {
              // Create unique field names for line items
              const fieldName = `material_${index}_${key}`;
              invalidFields.push(fieldName);
              // Add display names for estimate fields
              this.addEstimateFieldDisplayNames(fieldName, key, 'Material', index + 1);
            }
          });
        }
      });
    }

    // Update the validation tracker with estimate line item errors
    if (invalidFields.length > 0) {
      const currentInvalidFields = this.formValidationTracker.getCurrentInvalidFields();
      // Filter out previous estimate fields and add new ones
      const nonEstimateFields = currentInvalidFields
        .filter(field => !field.controlName.startsWith('part_') &&
                        !field.controlName.startsWith('labor_') &&
                        !field.controlName.startsWith('material_'))
        .map(field => field.controlName);

      this.formValidationTracker.updateInvalidFields([...nonEstimateFields, ...invalidFields]);
    }

    // If the form is valid so far, check the correction list.
    if (this.formCompleted) {
      this.formCompleted = this.estimateList.formCompleted;
    }
  }

  public hasLineItems(): boolean {
    return this.estimateList?.objectList?.length > 0 ?? false;
  }

  public removeAllLineItems(): void {
    this.confirmationDialog = this.dialog.open(DialogComponent, {
      data: {
        title: 'Delete All Line Items',
        bodyText: 'Are you sure you want to delete all line items for this repair estimate?',
        cancelButtonText: 'Cancel',
        confirmationButtonText: 'Yes, Delete All'
      }
    });

    this.confirmationDialog.afterClosed().subscribe(result => {
      if (result) {
        this.estimateList.objectList = [];
        this.estimateList.changeDetectorRef.detectChanges();
        // Clear estimate validation errors when all line items are removed
        this.clearEstimateValidationErrors();
        // Trigger global recalculation when all line items are removed
        EstimateTotalService.triggerGlobalRecalculation();
        // Trigger change detection to update the button visibility immediately
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Add display names for estimate line item fields
   */
  private addEstimateFieldDisplayNames(fieldName: string, controlKey: string, estimateType: string, lineNumber: number): void {
    const fieldDisplayNames: { [key: string]: string } = {
      'component': 'Component',
      'partNumber': 'Part Number',
      'etaDays': 'Part ETA',
      'price': 'Price',
      'quantity': 'Quantity',
      'laborOperation': 'Labor Operation',
      'rate': 'Labor Rate',
      'hours': 'Hours',
      'description': 'Description'
    };

    const baseDisplayName = fieldDisplayNames[controlKey] || controlKey;
    const displayName = `${estimateType} Line ${lineNumber} - ${baseDisplayName}`;
    this.formValidationTracker.addFieldDisplayName(fieldName, displayName);
  }

  /**
   * Clear estimate-related validation errors
   */
  private clearEstimateValidationErrors(): void {
    const currentInvalidFields = this.formValidationTracker.getCurrentInvalidFields();
    const nonEstimateFields = currentInvalidFields
      .filter(field => !field.controlName.startsWith('part_') &&
                      !field.controlName.startsWith('labor_') &&
                      !field.controlName.startsWith('material_'))
      .map(field => field.controlName);

    this.formValidationTracker.updateInvalidFields(nonEstimateFields);
  }
}
